import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { FileText, Upload, Trash2, Eye, Users, User, CheckCircle, Clock } from 'lucide-react';

interface Agreement {
  id: string;
  title: string;
  type: 'broker_agreement' | 'rate_confirmation';
  file_url: string;
  carrier_id?: string;
  status: string;
  created_at: string;
  carrier_name?: string;
  load_number?: string;
  delivery_status?: string;
  pod_file_url?: string;
  delivered_at?: string;
 signed_by_name?: string;
  signed_at?: string;
  signed_copy_url?: string;
}

const BrokerAgreementsPage = () => {
  const { profile } = useCompanyProfile();
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [carriers, setCarriers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeType, setActiveType] = useState<'broker_agreement' | 'rate_confirmation'>('broker_agreement');

  // Upload form
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadType, setUploadType] = useState<'broker_agreement' | 'rate_confirmation'>('broker_agreement');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile?.company_code) {
      fetchAgreements();
      fetchCarriers();
    }
  }, [profile?.company_code]);

  const fetchAgreements = async () => {
    try {
      const { data, error } = await supabase
        .from('agreements')
        .select('*')
        .eq('company_code', profile?.company_code)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get carrier names for rate confirmations
      if (data && data.length > 0) {
        const carrierIds = data.filter(a => a.carrier_id).map(a => a.carrier_id);
        if (carrierIds.length > 0) {
          const { data: carrierProfiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', carrierIds);

          const enriched = data.map(a => ({
            ...a,
            carrier_name: carrierProfiles?.find(p => p.id === a.carrier_id)
              ? `${carrierProfiles.find(p => p.id === a.carrier_id)?.first_name} ${carrierProfiles.find(p => p.id === a.carrier_id)?.last_name}`
              : undefined,
          }));
          setAgreements(enriched);
        } else {
          setAgreements(data);
        }
      } else {
        setAgreements([]);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const { data: apps } = await supabase
        .from('applications')
        .select('user_id')
        .eq('company_code', profile?.company_code);

      if (apps && apps.length > 0) {
        const userIds = apps.map(a => a.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);

        if (profiles) {
          setCarriers(profiles.map(p => ({
            id: p.id,
            name: `${p.first_name} ${p.last_name}`,
            email: p.email,
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadTitle.trim()) return;
    if (uploadType === 'rate_confirmation' && !selectedCarrier) {
      alert('Please select a carrier for the rate confirmation.');
      return;
    }

    setUploading(true);
    try {
      const fileName = `${profile?.company_code}/${uploadType}/${Date.now()}_${selectedFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('carrier-documents')
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from('agreements')
        .insert({
          company_code: profile?.company_code,
          uploaded_by: profile?.id,
          title: uploadTitle,
          type: uploadType,
          file_url: fileName,
          carrier_id: uploadType === 'rate_confirmation' ? selectedCarrier : null,
          status: 'active',
        });

      if (insertError) throw insertError;

      // Notify carrier — rate con or agreement assigned
      if (uploadType === 'rate_confirmation' && selectedCarrier) {
        await supabase.from('notifications').insert({
          company_code: profile?.company_code,
          recipient_id: selectedCarrier,
          title: 'New Rate Con Assigned',
          message: `You have a new rate confirmation to review: "${uploadTitle}"`,
          type: 'rate_con_assigned',
        });
      } else if (uploadType === 'broker_agreement') {
        // Notify all carriers about new broker agreement
        const { data: apps } = await supabase
          .from('applications')
          .select('user_id')
          .eq('company_code', profile?.company_code);
        if (apps && apps.length > 0) {
          const notifs = apps.map(a => ({
            company_code: profile?.company_code,
            recipient_id: a.user_id,
            title: 'New Agreement to Review',
            message: `A new broker agreement has been posted: "${uploadTitle}"`,
            type: 'rate_con_assigned',
          }));
          await supabase.from('notifications').insert(notifs);
        }
      }

      alert(`${uploadType === 'broker_agreement' ? 'Agreement' : 'Rate Confirmation'} uploaded successfully!`);
      setShowUploadForm(false);
      setUploadTitle('');
      setSelectedFile(null);
      setSelectedCarrier('');
      fetchAgreements();
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (fileUrl: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('carrier-documents')
        .createSignedUrl(fileUrl, 3600);

      if (error || !data?.signedUrl) {
        alert('Unable to load document');
        return;
      }
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this?')) return;

    try {
      const { error } = await supabase
        .from('agreements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setAgreements(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete.');
    }
  };

  const handleArchive = async (id: string) => {
    try {
      const { error } = await supabase
        .from('agreements')
        .update({ status: 'archived' })
        .eq('id', id);
      if (error) throw error;
      setAgreements(prev => prev.map(a => a.id === id ? { ...a, status: 'archived' } : a));
    } catch (error) {
      console.error('Error archiving:', error);
      alert('Failed to archive.');
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });

  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredAgreements = agreements.filter(a => {
    if (a.type !== activeType) return false;
    if (showArchived ? a.status !== 'archived' : a.status === 'archived') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (a.title || '').toLowerCase().includes(q)
        || (a.carrier_name || '').toLowerCase().includes(q)
        || (a.load_number || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Rate Cons & Agreements</h1>
        <button
          onClick={() => { setShowUploadForm(true); setUploadType(activeType); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Upload className="h-4 w-4" /> {activeType === 'rate_confirmation' ? 'Send Rate Con' : 'Send Agreement'}
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by title, carrier name, or load number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Type Tabs */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveType('broker_agreement')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeType === 'broker_agreement'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4" /> Broker Agreements
          </span>
        </button>
        <button
          onClick={() => setActiveType('rate_confirmation')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeType === 'rate_confirmation'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-1.5">
            <User className="h-4 w-4" /> Rate Confirmations
          </span>
        </button>
      </div>

      {/* Active / Archived Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowArchived(false)}
          className={`px-3 py-1.5 text-xs rounded-md font-medium ${!showArchived ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          Active
        </button>
        <button
          onClick={() => setShowArchived(true)}
          className={`px-3 py-1.5 text-xs rounded-md font-medium ${showArchived ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          📁 Archived
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          {activeType === 'broker_agreement'
            ? 'Upload your Broker-Carrier Agreement template. This will be available to all your carriers for review and signing.'
            : 'Upload rate confirmations for specific carriers. Each carrier will receive their own copy to review and sign.'}
        </p>
      </div>

      {/* Agreements List */}
      {filteredAgreements.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No {activeType === 'broker_agreement' ? 'agreements' : 'rate confirmations'} uploaded yet.</p>
          <button
            onClick={() => { setShowUploadForm(true); setUploadType(activeType); }}
            className="mt-3 text-blue-600 hover:underline text-sm"
          >
            Upload your first one
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {filteredAgreements.map((agreement) => (
            <div key={agreement.id} className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{agreement.title}</p>
                  <p className="text-xs text-gray-500">
                    Uploaded {formatDate(agreement.created_at)}
                    {agreement.carrier_name && ` • For: ${agreement.carrier_name}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  agreement.delivery_status === 'pod_submitted' ? 'bg-emerald-100 text-emerald-700'
                    : agreement.delivery_status === 'delivered' ? 'bg-orange-100 text-orange-700'
                    : agreement.status === 'signed' ? 'bg-green-100 text-green-700'
                    : agreement.status === 'declined' ? 'bg-red-100 text-red-700'
                    : agreement.status === 'archived' ? 'bg-gray-100 text-gray-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {agreement.delivery_status === 'pod_submitted' ? '📄 Delivered — POD Attached'
                    : agreement.delivery_status === 'delivered' ? '🚚 Delivered'
                    : agreement.status === 'signed' ? '✓ Signed & Accepted'
                    : agreement.status === 'declined' ? '✗ Declined'
                    : agreement.status === 'archived' ? '📁 Archived'
                    : '⏳ Awaiting Signature'}
                </span>
                {agreement.pod_file_url && (
                  <button
                    onClick={() => handleView(agreement.pod_file_url!)}
                    className="flex items-center px-3 py-1.5 text-xs bg-orange-50 text-orange-700 rounded-md hover:bg-orange-100 font-medium"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> View POD
                  </button>
                )}
               {agreement.status === 'signed' && agreement.signed_copy_url && (
                  <button
                    onClick={() => handleView(agreement.signed_copy_url!)}
                    className="flex items-center px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100 font-medium"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> View Signed Rate Con
                  </button>
                )}
                <button
                  onClick={() => handleView(agreement.file_url)}
                  className="flex items-center px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 font-medium"
                >
                  <Eye className="h-3.5 w-3.5 mr-1" /> View Original
                </button>
                {agreement.status !== 'archived' ? (
                  <button
                    onClick={() => handleArchive(agreement.id)}
                    className="flex items-center px-3 py-1.5 text-xs bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 font-medium"
                  >
                    📁 Archive
                  </button>
                ) : null}
                <button
                  onClick={() => handleDelete(agreement.id)}
                  className="flex items-center px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-md hover:bg-red-100 font-medium"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Upload {uploadType === 'broker_agreement' ? 'Agreement' : 'Rate Confirmation'}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as 'broker_agreement' | 'rate_confirmation')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="broker_agreement">Broker Agreement (All Carriers)</option>
                <option value="rate_confirmation">Rate Confirmation (Specific Carrier)</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder={uploadType === 'broker_agreement' ? 'e.g. Broker-Carrier Agreement 2026' : 'e.g. Rate Confirmation - Chicago to Dallas'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {uploadType === 'rate_confirmation' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Carrier *</label>
                <select
                  value={selectedCarrier}
                  onChange={(e) => setSelectedCarrier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select a carrier...</option>
                  {carriers.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">PDF File *</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowUploadForm(false); setUploadTitle(''); setSelectedFile(null); setSelectedCarrier(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !uploadTitle.trim() || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerAgreementsPage;
